// Hosted Model Serving (Part II) — pure view logic for Admin → Model Serving.
// Everything the page, dialogs and drawer DECIDE lives here (unit-tested); the
// components only render it. Spec: docs/design/hosted-model-serving-v2-ui-acceptance.md
// (mockup: hosted-model-serving-ui-mockup-v1.html).

const GB = 1e9;
const GPU_TYPE = { a10g: "A10G", l40s: "L40S", h100: "H100", cpu: "CPU" };

export const IDLE_CHOICES = [
  { value: 5, label: "5 min" }, { value: 15, label: "15 min" }, { value: 30, label: "30 min" },
  { value: 60, label: "1 hour" }, { value: 240, label: "4 hours" },
];

// ─── formatting ─────────────────────────────────────────────────────────────

/** A version's size, or words when it was never measured (never "0 GB"). */
export function sizeText(bytes) {
  return bytes == null ? "size not measured yet" : fmtGB(bytes);
}

export function fmtGB(bytes) {
  if (bytes == null) return "—";
  if (bytes === 0) return "0 GB";
  const g = bytes / GB;
  if (g >= 10) return `${Math.round(g)} GB`;
  if (g >= 0.1) return `${g.toFixed(1)} GB`;
  return `${Math.max(1, Math.round(bytes / 1e6))} MB`;
}

export function shortSha(sha) {
  if (!sha) return "—";
  return sha === "legacy" ? "legacy" : String(sha).slice(0, 7);
}

export function duration(seconds) {
  if (seconds == null) return "—";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m} m ${s % 60} s` : `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
}

export function timeAgo(iso, now = Date.now()) {
  if (!iso) return "—";
  const t = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  const d = Math.round(s / 86400);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

export function money(n) {
  if (n == null) return "—";
  return `$${Number(n).toFixed(2)}`;
}

export function formatName(q) {
  return q ? String(q).toUpperCase() : "—";
}

/** A pasted repo id or huggingface.co URL → "owner/name" ("" when it isn't one). */
export function parseRepo(input) {
  let s = String(input || "").trim();
  s = s.replace(/^https?:\/\/(www\.)?huggingface\.co\//i, "").replace(/[?#].*$/, "");
  const parts = s.split("/").filter(Boolean);
  if (parts.length < 2) return "";
  return `${parts[0]}/${parts[1]}`;
}

export function gpuType(nodeClass) {
  const prefix = String(nodeClass || "").split("-")[0];
  return GPU_TYPE[prefix] || prefix.toUpperCase() || "GPU";
}

/** "4× L40S" — or just "CPU" for a preset without GPUs (kind). */
export function gpuName(opt) {
  const count = opt.gpu_count ?? opt.gpus;
  const type = gpuType(opt.key || opt.node_class);
  return count ? `${count}× ${type}` : type;
}

/** "4× L40S · 192 GB" for a GPU option / preset ({key, gpu_count, vram_gb}). */
export function presetLabel(opt) {
  if (!opt) return "—";
  return `${gpuName(opt)} · ${opt.vram_gb} GB`;
}

export function shortInstance(node) {
  return String(node || "").replace(/xlarge$/, "xl");
}

// ─── model state ────────────────────────────────────────────────────────────

const WARMING_PHASES = ["provisioning", "waiting_capacity", "copying", "loading"];

/** The one serving state of a hosted model, from its shared status (the same
 * status Model Serving, Local LLM and the agent chat read): running | warming |
 * warm_failed | parked | unreachable. ``warmFailure``: a failed warm-up that was
 * parked again, when the status itself didn't carry it. */
export function servingState(serving, warmFailure) {
  const phase = serving?.phase;
  if (phase === "failed") return "warm_failed";
  if (phase === "ready") return "running";
  if (WARMING_PHASES.includes(phase)) return "warming";
  if (phase === "unavailable") return "unreachable";
  return warmFailure ? "warm_failed" : "parked";
}

/** One state per model, for the badge, the actions and which section it's in. */
export function modelStatus(m) {
  if (!m) return "draft";
  if (m.serving_mode !== "aws_on_demand") return "local";
  if (m.state === "downloading") return "downloading";
  if (m.state === "failed") return "download_failed";
  if (m.state !== "ready") return "draft";
  return servingState(m.serving, m.warm_failure);
}

const BADGES = {
  running: { label: "Running", tone: "green" },
  warming: { label: "Warming", tone: "amber" },
  parked: { label: "Parked · $0", tone: "dim" },
  downloading: { label: "Downloading to S3", tone: "purple" },
  download_failed: { label: "Download failed", tone: "pink" },
  warm_failed: { label: "Warm-up failed", tone: "pink" },
  draft: { label: "Draft", tone: "dim" },
  unreachable: { label: "Unreachable", tone: "pink" },
  local: { label: "Always on", tone: "cyan" },
};

export function statusBadge(status) {
  return BADGES[status] || BADGES.draft;
}

/** True while a model's server is still scaled up — holding a GPU or still asking
 * for one. A failed warm-up the platform hasn't stopped yet is one (B-44). */
export function stillScaledUp(model) {
  return Boolean(model?.serving?.scaled_up);
}

/** The library row's actions (C3–C8); Warm up is never offered while downloading
 * or after a failed download (C9). A model whose only download was cancelled can
 * be retried or removed. A failed warm-up offers Stop while it's still scaled up,
 * Try again once it's parked. */
export function rowActions(status, model) {
  switch (status) {
    case "running":
    case "warming": return ["details", "stop"];
    case "parked": return ["details", "warm"];
    case "downloading": return ["details", "cancel_download"];
    case "download_failed": return ["remove", "retry_download"];
    case "warm_failed": return stillScaledUp(model) ? ["details", "stop"] : ["details", "retry_warm"];
    case "draft": return retryableRevision(model) ? ["remove", "retry_download"] : ["details", "remove"];
    default: return ["details"];
  }
}

export function activeRevision(m) {
  return (m?.revisions || []).find((r) => r.id === m.active_revision_id) || null;
}

export function inFlightRevision(m) {
  return (m?.revisions || []).find((r) => ["queued", "downloading", "verifying"].includes(r.status)) || null;
}

export function failedRevision(m) {
  return (m?.revisions || []).find((r) => r.status === "failed") || null;
}

/** The newest failed or cancelled download (what "Retry" restarts). */
export function retryableRevision(m) {
  return (m?.revisions || []).find((r) => r.status === "failed" || r.status === "cancelled") || null;
}

/** Download progress for a revision: {pct, text} ("40.3 / 65.5 GB · 11 / 17 files · ~6 min left"). */
export function downloadProgress(rev, now = Date.now()) {
  if (!rev) return { pct: 0, text: "" };
  const total = rev.bytes_total || 0;
  const done = rev.bytes_done || 0;
  const pct = total ? Math.min(100, Math.floor((done / total) * 100)) : 0;
  const gb = (b) => (b / GB).toFixed(1);
  let text = `${gb(done)} / ${gb(total)} GB`;
  if (rev.files_total) text += ` · ${rev.files_done || 0} / ${rev.files_total} files`;
  const started = Date.parse(`${(rev.created_at || "").replace(/Z$/, "")}Z`);
  const elapsed = (now - started) / 1000;
  if (done > 0 && total > done && elapsed > 0) {
    const left = ((total - done) / (done / elapsed)) / 60;
    text += ` · ~${Math.max(1, Math.round(left))} min left`;
  }
  return { pct, text };
}

// ─── page stats (A5–A8) ─────────────────────────────────────────────────────

export function pageStats(models, presets = {}) {
  const list = Array.isArray(models) ? models : [];
  const st = list.map(modelStatus);
  const running = st.filter((s) => s === "running").length;
  const warming = st.filter((s) => s === "warming").length;
  // A machine exists (and costs money) once the model is copying, loading or ready —
  // not while it's still waiting for AWS to provide one (staging 2026-10-08:
  // "$5.67/hr · 1 GPU node active" with no machine because AWS had no capacity).
  const live = list.filter((m, i) => st[i] === "running"
    || (st[i] === "warming" && ["copying", "loading"].includes(m.serving?.phase)));
  const withWeights = list.flatMap((m) => (m.revisions || []).filter((r) => ["ready", "superseded"].includes(r.status)));
  const timeouts = list.filter((m) => m.serving_mode === "aws_on_demand").map((m) => m.idle_timeout_minutes || 15);
  return {
    running, warming,
    gpuNodes: live.length,
    library: list.length,
    ready: st.filter((s) => ["running", "warming", "parked", "warm_failed"].includes(s)).length,
    downloading: st.filter((s) => s === "downloading").length,
    failed: st.filter((s) => s === "download_failed" || s === "warm_failed").length,
    s3Bytes: withWeights.reduce((n, r) => n + (r.bytes_total || 0), 0),
    versions: withWeights.length,
    unsized: withWeights.filter((r) => r.bytes_total == null).length,
    spendPerHour: live.reduce((n, m) => n + (presets[m.node_class]?.price_per_hour || 0), 0),
    idleMinutes: timeouts.length ? Math.min(...timeouts) : 15,
  };
}

// ─── warm-up (B4–B6) ────────────────────────────────────────────────────────

/** The warm-up checklist, as the server words it (incl. "· 64 / 121 GB" on the
 * copy step) — the same text every page shows. */
export function warmSteps(serving) {
  return serving?.steps || [];
}

export function parksText(serving, idleMinutes) {
  if (serving?.keep_warm) return `Kept warm · never parks on its own`;
  const s = serving?.parks_in_seconds;
  if (s == null) return `Idle timeout ${idleMinutes} min`;
  return `Parks in ${Math.max(1, Math.round(s / 60))} min if no requests · idle timeout ${idleMinutes} min`;
}

// ─── GPU choice (E5, F2–F4, G2–G5) ──────────────────────────────────────────

export function gpuOptionLabel(opt, { recommended } = {}) {
  let label = presetLabel(opt);
  if (opt.key === recommended) label += " — recommended";
  else if (!opt.fits) label += " — too small for this model";
  return label;
}

export function fitHint(rec, opt) {
  if (!rec?.vram_required_gb || !opt) return "";
  const spare = opt.vram_gb - rec.vram_required_gb;
  const base = `Fits: ~${rec.vram_required_gb} GB needed of ${opt.vram_gb} GB`;
  return spare > rec.vram_required_gb ? `${base} (more than needed).` : `${base}.`;
}

export function needsText(model, rec) {
  const params = model?.params_billions;
  const q = formatName(rec?.quantization || model?.quantization);
  if (!params || !rec?.vram_required_gb) return "";
  return `${paramCount(params)} at ${q} needs ~${rec.vram_required_gb} GB of GPU memory.`;
}

export function recommendationText(rec) {
  if (!rec) return "";
  if (!rec.node_class || !rec.fits) {
    return (rec.warnings || []).join(" ") || "No GPU this cluster offers can hold this model.";
  }
  const label = gpuName(rec);
  return `Recommended: ${label} (${rec.node}, ${rec.vram_available_gb} GB). `
    + `${formatName(rec.quantization)} needs ~${rec.vram_required_gb} GB of GPU memory. `
    + `About ${money(rec.price_per_hour)}/hr while running (AWS on-demand, approx.), $0 when parked.`;
}

/** "124B", or "1M" for a sub-billion model (never "0B"). */
export function paramCount(billions) {
  const b = Number(billions);         // callers pass a known size
  return b >= 1 ? `${Math.round(b)}B` : `${Math.max(1, Math.round(b * 1000))}M`;
}

export function paramsText(model) {
  if (!model?.params_billions) return "—";
  const total = paramCount(model.params_billions);
  return model.active_params_billions ? `${total} · ${paramCount(model.active_params_billions)} active` : total;
}

export function accessBadge(model) {
  if (model?.private) return "Private";
  if (model?.gated) return "Gated";
  return "Public";
}

/** Settings → the POST /catalog body. Extra args are whitespace-split. */
export function buildAddBody(f) {
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const args = String(f.extra_args || "").trim();
  const body = {
    repo: f.repo, revision: f.revision || "main", key: (f.key || "").trim() || null,
    display_name: (f.display_name || "").trim() || null, node_class: f.node_class || null,
    image: (f.image || "").trim() || null, max_model_len: num(f.max_model_len),
    extra_args: args ? args.split(/\s+/) : [],
  };
  return Object.fromEntries(Object.entries(body).filter(([, v]) => v !== null));
}

// ─── versions (K2–K7, L3–L5) ────────────────────────────────────────────────

export function versionRows(model, now = Date.now()) {
  const rows = [];
  for (const r of model?.revisions || []) {
    if (["cancelled", "deleting"].includes(r.status)) continue;
    const active = r.id === model.active_revision_id;
    const pending = r.id === model.pending_revision_id;
    const inflight = ["queued", "downloading", "verifying"].includes(r.status);
    const p = downloadProgress(r, now);
    let title; let meta; let badge; let actions = [];
    if (inflight) {
      title = `${shortSha(r.sha)} · downloading ${p.pct}%`;
      meta = `Update started by ${r.created_by_name || "someone"} ${timeAgo(r.created_at, now)} · ${p.text}`;
      badge = "Downloading"; actions = ["cancel"];
    } else if (active) {
      title = `${shortSha(r.sha)} · in use`;
      meta = `Added ${String(r.ready_at || r.created_at || "").slice(0, 10)} · ${fmtGB(r.bytes_total)}`;
      badge = "Active";
    } else if (r.status === "failed") {
      title = `${shortSha(r.sha)} · download failed`;
      meta = r.error || "The download failed.";
      badge = "Failed"; actions = ["retry", "delete"];
    } else {
      title = shortSha(r.sha);
      meta = pending ? "Downloaded · waiting to switch" : "Previous version · kept for rollback";
      badge = pending ? "Ready" : "";
      actions = pending ? ["switch", "delete"] : ["rollback", "delete"];
    }
    rows.push({ id: r.id, title, meta, badge, actions, progress: inflight ? p.pct : null });
  }
  return rows;
}

export function pendingSwitchText(model, serving) {
  const rev = (model?.revisions || []).find((r) => r.id === model?.pending_revision_id);
  if (!rev) return "";
  const when = model.switch_policy === "when_idle"
    ? (serving?.parks_in_seconds != null
      ? ` (in ~${Math.max(1, Math.round(serving.parks_in_seconds / 60))} min if no requests)` : "")
    : "";
  return model.switch_policy === "when_idle"
    ? `${shortSha(rev.sha)} will replace the current version when the model next parks${when}.`
    : `${shortSha(rev.sha)} is downloaded. The model is running — choose when to switch.`;
}

// ─── activity (M1–M3) ───────────────────────────────────────────────────────

const EVENT_TEXT = {
  added: () => "Added from Hugging Face",
  download_started: (d) => `Download started${d.bytes_total ? ` (${fmtGB(d.bytes_total)})` : ""}`,
  download_finished: (d) => `Version downloaded (${fmtGB(d.bytes_total)})`,
  download_failed: (d) => `Download failed: ${d.reason || "unknown reason"}`,
  download_cancelled: () => "Download cancelled",
  warm_requested: (d, e) => (e.actor_type === "agent"
    ? "Warmed up by a request" : `Warm-up started${d.node_class ? ` on ${gpuType(d.node_class)}` : ""}`),
  ready: (d) => `Ready${d.cold_start_s != null ? ` in ${duration(d.cold_start_s)}` : ""}`,
  warm_failed: (d) => `Warm-up failed: ${d.reason || "unknown reason"}`,
  stopped: () => "Stopped",
  auto_parked: (d) => `Parked automatically after ${d.idle_timeout_minutes || 15} min with no requests`,
  update_requested: (d) => `Update to ${shortSha(d.sha)} started`,
  already_current: () => "Update checked — already on this version",
  switched: (d) => `Switched version${d.reason === "applied when idle" ? " (when idle)" : ""}`,
  switch_pending: () => "New version ready — switch pending",
  switch_cancelled: () => "Pending switch cancelled",
  rolled_back: () => "Rolled back to the previous version",
  version_deleted: (d) => `Version ${shortSha(d.sha)} deleted${d.reason ? ` (${d.reason})` : ""}`,
  settings_changed: () => "Settings changed",
  removed: () => "Removed",
  keep_warm_changed: (d) => (d.keep_warm ? "Set to keep warm" : "Keep warm turned off"),
};

export function eventLine(e) {
  const d = e.detail || {};
  const what = (EVENT_TEXT[e.kind] || (() => e.kind))(d, e);
  let by;
  if (e.actor_type === "platform") by = "platform";
  else if (e.actor_type === "agent") by = e.actor_name ? `agent “${e.actor_name}”` : "an agent request";
  else by = e.actor_name || "a user";
  const error = ["download_failed", "warm_failed"].includes(e.kind);
  return { id: e.id, when: e.created_at, what, by, error };
}

// ─── endpoint / remove (N, P) ───────────────────────────────────────────────

export function usedByItems(usage) {
  const rankings = (usage?.rankings || []).map((r) => ({
    kind: "ranking", label: `${r.purpose[0].toUpperCase()}${r.purpose.slice(1)} ranking · position ${r.position}`,
  }));
  const agents = (usage?.agents || []).map((a) => ({ kind: "agent", id: a.id, label: a.name }));
  return [...rankings, ...agents];
}

export function removeBlockText(usage) {
  const items = usedByItems(usage);
  if (!items.length) return "";
  const agents = items.filter((i) => i.kind === "agent").map((i) => i.label);
  const rankings = items.filter((i) => i.kind === "ranking").length;
  const parts = [];
  if (rankings) parts.push(rankings === 1 ? "a ranking" : `${rankings} rankings`);
  if (agents.length) parts.push(`${agents.length} agent${agents.length === 1 ? "" : "s"} (${agents.join(", ")})`);
  return `${parts.join(" and ")} use${items.length === 1 ? "s" : ""} this model.`;
}

export function worksWith(model) {
  return model?.text_only ? "Chat · text only" : "Chat · text and images";
}

// ─── GPU fit details (F4, G3) ───────────────────────────────────────────────

/** The short reason a preset can't hold the model: "too small — 96 GB", "A10G can't run FP8". */
export function optionReason(opt, quantization) {
  if (!opt || opt.fits) return "";
  const r = String(opt.reason || "");
  if (r.includes("can't run")) return `${gpuType(opt.key)} can't run ${formatName(quantization)}`;
  if (r.includes("node disk")) return `not enough disk — ${opt.disk_gb} GB`;
  return `too small — ${opt.vram_gb} GB`;
}

/** F4 — why the picked GPU can't hold the model, in one sentence. */
export function fitBlockText(model, opt) {
  if (!opt || opt.fits) return "";
  const q = formatName(model?.quantization);
  const label = gpuName(opt);
  const reason = String(opt.reason || "");
  const parts = [];
  if (model?.params_billions && opt.vram_required_gb > opt.vram_gb) {
    parts.push(`${paramCount(model?.params_billions)} at ${q} needs ~${opt.vram_required_gb} GB but ${label} has ${opt.vram_gb} GB`);
  }
  if (reason.includes("can't run")) parts.push(`${gpuType(opt.key)} can't run ${q}`);
  else if (reason.includes("node disk")) parts.push(reason);
  if (!parts.length) parts.push(reason || `${label} can't hold this model`);
  return `${parts.join(", and ")}. Pick a larger GPU.`;
}

// ─── model facts (E4) ───────────────────────────────────────────────────────

export function formatText(model) {
  const q = model?.quantization;
  if (!q) return "BF16";
  return ["fp8", "nvfp4"].includes(q) ? `${formatName(q)} (pre-quantized)` : formatName(q);
}

export function archText(model) {
  const arch = (model?.architectures || [])[0] || "—";
  return model?.moe ? `${arch} · MoE` : arch;
}

// ─── running card + library row (B7, C2, C6–C8) ─────────────────────────────

export function gpuText(model, preset) {
  if (!preset) return gpuType(model?.node_class);
  return `${gpuName(preset)} · ${shortInstance(preset.node)}`;
}

export function cardSpecs(model, preset, now = Date.now()) {
  const rev = activeRevision(model);
  const base = [["GPU", gpuText(model, preset)],
    ["Weights", `${sizeText(rev?.bytes_total)} · ${model?.quantization || "bf16"}`]];
  if (modelStatus(model) === "warming") {
    return [...base, ["Source", "your S3 library"], ["Started", timeAgo(model?.serving?.started_at, now)]];
  }
  return [...base, ["Requests (1h)", String(model?.requests_1h ?? 0)],
    ["Last used", timeAgo(model?.serving?.last_used_at, now)]];
}

/** What the "Weights in S3" cell shows for one model. */
export function weightsCell(model, now = Date.now()) {
  const status = modelStatus(model);
  if (status === "downloading") {
    const p = downloadProgress(inFlightRevision(model), now);
    return { text: `Downloading… ${p.pct}%`, progress: p.pct, meta: p.text };
  }
  if (status === "download_failed") {
    const rev = failedRevision(model);
    return { text: `Stopped at ${downloadProgress(rev, now).pct}%`, error: rev?.error || "The download failed." };
  }
  const ready = `${sizeText(activeRevision(model)?.bytes_total)} · ready`;
  if (status === "warm_failed") return { text: ready, error: warmFailureText(model) };
  if (status === "draft") {
    return retryableRevision(model) ? { text: "Download cancelled", error: "Retry to download it again, or remove the model." }
      : { text: "—" };
  }
  return { text: ready };
}

const _sentence = (s) => String(s || "").trim().replace(/\.+$/, "");

/** "Warm-up failed: <why>. <what to do>" — live from the pod, or from the last
 * warm-up's recorded reason once the failed warm-up was parked. */
export function warmFailureText(model) {
  const s = model?.serving;
  if (s?.phase === "failed" && s.detail) {
    return `Warm-up failed: ${_sentence(s.detail)}.`;          // "<why>. <what to do>" from the server
  }
  const why = _sentence(model?.warm_failure?.reason) || "unknown reason";
  return `Warm-up failed: ${why}. Try again or pick another GPU.`;
}

export function versionText(model) {
  const rev = activeRevision(model) || inFlightRevision(model) || failedRevision(model);
  return shortSha(rev?.sha);
}

/** The drawer header buttons (I3). */
export function drawerActions(status, model) {
  if (status === "running" || status === "warming") return { stop: true, warm: null };
  if (status === "warm_failed" && stillScaledUp(model)) return { stop: true, warm: null };
  if (status === "warm_failed") return { stop: false, warm: { label: "▶ Try again", disabled: false } };
  if (status === "parked") return { stop: false, warm: { label: "▶ Warm up", disabled: false } };
  return { stop: false, warm: { label: "▶ Warm up", disabled: true } };
}

// ─── overview (J1–J6) ───────────────────────────────────────────────────────

function latency(ms) {
  if (ms == null) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`;
}

/** Under "Last cold start": the GPU · copy · load split, or why there isn't one —
 * never "not warmed up yet" next to a time (B-46). */
export function coldStartSub(m) {
  const b = m?.cold_start_breakdown;
  if (b) return `GPU ${duration(b.gpu_s)} · S3 copy ${duration(b.copy_s)} · load ${duration(b.load_s)}`;
  return m?.last_cold_start_s == null ? "not warmed up yet" : "split not recorded";
}

export function overviewMetrics(x) {
  const m = x || {};
  const n = m.requests_24h || 0;
  const versions = m.versions || 0;
  return [
    { key: "requests", label: "Requests · 24h", value: n.toLocaleString("en-US"), sub: `peak ${m.peak_per_hour || 0}/h` },
    { key: "p95", label: "p95 latency", value: latency(m.p95_ms), sub: "whole reply, as agents see it" },
    { key: "errors", label: "Error rate", value: m.error_rate == null ? "—" : `${(m.error_rate * 100).toFixed(1)}%`,
      sub: n ? `${m.errors_24h || 0} of ${n.toLocaleString("en-US")}` : "no requests yet" },
    { key: "cold", lc: "cold-start", label: "Last cold start", value: duration(m.last_cold_start_s), sub: coldStartSub(m) },
    { key: "gpu", lc: "cost-month", label: "GPU time · this month", value: `${m.gpu_hours_month || 0} h`,
      sub: `≈ ${money(m.gpu_cost_month || 0)}` },
    { key: "s3", label: "S3 storage", value: m.sizes_unknown && !m.storage_bytes ? "—" : fmtGB(m.storage_bytes || 0),
      sub: `${versions} version${versions === 1 ? "" : "s"}${m.sizes_unknown ? ` · ${m.sizes_unknown} not measured yet` : ""}` },
  ];
}

// ─── activity time + logs (M1, M4) ──────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "10:42" today, "Mon" this week, "Sep 6" earlier (UTC). */
export function eventTime(iso, now = Date.now()) {
  if (!iso) return "—";
  const t = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(t)) return "—";
  const d = new Date(t);
  const today = new Date(now);
  if (d.toISOString().slice(0, 10) === today.toISOString().slice(0, 10)) return d.toISOString().slice(11, 16);
  if (now - t < 6 * 86400e3) return DAYS[d.getUTCDay()];
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** The server logs + Kubernetes events as one readable block. */
export function logsText(logs) {
  const pods = logs?.pods || [];
  if (!pods.length) return "No server is running for this model right now — logs appear while it warms up or serves.";
  const out = [];
  for (const p of pods) {
    out.push(`── ${p.pod} ──`);
    for (const e of p.events || []) out.push(`${e.time || ""}  ${e.reason || ""}  ${e.message || ""}`.trim());
    for (const [name, text] of Object.entries(p.containers || {})) {
      out.push(`── ${name} ──`, String(text || "").trimEnd());
    }
  }
  return out.join("\n");
}

// ─── settings (O1–O5) ───────────────────────────────────────────────────────

export function settingsForm(model) {
  return {
    idle_timeout_minutes: String(model?.idle_timeout_minutes || 15),
    max_model_len: model?.max_model_len ? String(model.max_model_len) : "",
    max_num_seqs: model?.max_num_seqs ? String(model.max_num_seqs) : "",
    image: model?.image || "",
    extra_args: (model?.extra_args || []).join(" "),
  };
}

/** Only what changed, typed for PATCH /catalog/{key}. */
export function settingsChanges(model, form) {
  const before = settingsForm(model);
  const out = {};
  for (const k of Object.keys(before)) {
    if (form[k] === before[k]) continue;
    if (k === "extra_args") out[k] = String(form[k]).trim() ? String(form[k]).trim().split(/\s+/) : [];
    else if (k === "image") out[k] = String(form[k]).trim();
    else if (String(form[k]).trim() !== "") out[k] = Number(form[k]);
  }
  return out;
}

// ─── update (L3, L4) ────────────────────────────────────────────────────────

export function updateCheck(found, model, now = Date.now()) {
  const current = activeRevision(model);
  const sha = found?.sha;
  if (!sha) return null;
  if (current && current.sha === sha) return { same: true };
  return {
    same: false,
    text: `Version ${shortSha(sha)} · published ${timeAgo(found.last_modified, now)} · ${fmtGB(found.bytes_total)} · ${found.files_total || 0} files.`,
    current: shortSha(current?.sha),
  };
}
